import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Rocket, Plus, Trash2, MapPin, Building2, FileText, Users, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";

/**
 * NewProjectPage — Form New Project full-page ala tab Data Proyek (Fase 4,
 * 2026-06-19 user request). 5 section konsisten dengan Data Proyek:
 *   Informasi Project · Lokasi Project · Ruang Lingkup Pekerjaan ·
 *   Kontak Project · Dokumen Project
 * - Action bar sticky: Batal / Simpan Draft / Buat Project.
 * - Validasi field wajib tampil dekat field terkait; data form TIDAK hilang
 *   saat validasi gagal.
 * - Kode proyek auto (preview, tidak bisa diedit).
 * - Anti double-submit (guard ref + disable).
 * - Sukses → redirect ke /projects/{id}/monitoring?tab=data-proyek.
 * - Draft disimpan ke localStorage (restore otomatis saat kembali).
 */
const DRAFT_KEY = "ens_new_project_draft_v1";

const EMPTY = {
  project_code: "", project_name: "", client: "", contract_number: "", contract_value: "",
  pic_name: "", pic_finance_name: "", start_date: "", end_date: "",
  address: "", province: "", city: "", plus_code: "",
  scope_of_work: [], client_contacts: [], site_name: "", site_phone: "", site_email: "",
  project_documents: [],
};

const FieldErr = ({ msg }) => msg ? <div className="text-[10px] text-rose-600 mt-0.5" role="alert">{msg}</div> : null;

const Inp = ({ testid, label, required, error, ...props }) => (
  <div>
    <label className="overline text-[9px] text-slate-600">{label}{required && <span className="text-rose-600 ml-0.5">*</span>}</label>
    <input data-testid={testid} {...props}
      className={`mt-1 w-full border px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:border-[#0B1D3A] ${error ? "border-rose-400" : "border-slate-300"}`} />
    <FieldErr msg={error} />
  </div>
);

const Section = ({ icon: Icon, title, children, testid }) => (
  <section className="bg-white border border-slate-200" data-testid={testid}>
    <header className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-2">
      <Icon size={14} className="text-[#0B1D3A]" />
      <h2 className="text-sm font-semibold text-[#0B1D3A]">{title}</h2>
    </header>
    <div className="p-4">{children}</div>
  </section>
);

export default function NewProjectPage() {
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  // 2026-06-20: daftar user untuk dropdown PIC Project (divisi Project saja) & PIC Keuangan (semua user aktif).
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);
  const [draftAt, setDraftAt] = useState(null);
  const submitGuard = useRef(false);

  // Restore draft
  useEffect(() => {
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (d && (d.project_name || d.client)) {
        setForm({ ...EMPTY, ...d.data });
        setDraftAt(d.saved_at);
      }
    } catch { /* noop */ }
  }, []);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const picProjectOptions = useMemo(() => users.filter((u) => u.division === "Project" && u.is_active !== false && !u.disabled_at).map((u) => u.name).filter(Boolean).sort((a, b) => a.localeCompare(b)), [users]);
  const picFinanceOptions = useMemo(() => users.filter((u) => u.is_active !== false && !u.disabled_at).map((u) => u.name).filter(Boolean).sort((a, b) => a.localeCompare(b)), [users]);

  const validate = () => {
    const e = {};
    if (!form.project_name.trim()) e.project_name = "Nama proyek wajib diisi.";
    if (!form.client.trim()) e.client = "Client wajib diisi.";
    if (!form.start_date) e.start_date = "Tanggal mulai wajib diisi.";
    if (form.end_date && form.start_date && form.end_date < form.start_date) e.end_date = "Tanggal selesai tidak boleh sebelum tanggal mulai.";
    if (form.contract_value && isNaN(Number(form.contract_value))) e.contract_value = "Nilai kontrak harus angka.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const saveDraft = () => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ data: form, saved_at: new Date().toISOString() }));
    setDraftAt(new Date().toISOString());
    toast.success("Draft tersimpan di browser ini.");
  };

  const submit = async () => {
    if (submitGuard.current || busy) return; // anti double-submit
    if (!validate()) {
      toast.error("Periksa kembali field yang wajib diisi.");
      return;
    }
    submitGuard.current = true;
    setBusy(true);
    try {
      const payload = {
        project_type: "new_project",
        project_code: form.project_code.trim() || null,
        project_name: form.project_name.trim(),
        client: form.client.trim(),
        contract_number: form.contract_number.trim() || null,
        contract_value: form.contract_value ? Number(form.contract_value) : null,
        pic_name: form.pic_name.trim() || null,
        pic_finance_name: form.pic_finance_name.trim() || null,
        start_date: form.start_date,
        end_date: form.end_date || null,
        province: form.province.trim() || null,
        city: form.city.trim() || null,
        location: (form.address || form.plus_code) ? {
          address: form.address.trim() || null,
          province: form.province.trim() || null,
          city: form.city.trim() || null,
          plus_code: form.plus_code.trim() || null,
        } : null,
        scope_of_work: form.scope_of_work.filter((s) => s.description?.trim()),
        client_contacts: form.client_contacts.filter((c) => c.name?.trim()),
        site_contact: (form.site_name || form.site_phone || form.site_email) ? {
          name: form.site_name.trim() || null, phone: form.site_phone.trim() || null, email: form.site_email.trim() || null,
        } : null,
        project_documents: form.project_documents.filter((d) => d.name?.trim()),
      };
      const { data } = await api.post("/active-projects", payload);
      localStorage.removeItem(DRAFT_KEY);
      toast.success(`Project "${data.project_name}" berhasil dibuat.`);
      nav(`/projects/${data.id}/monitoring?tab=data-proyek`);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal membuat project.");
      submitGuard.current = false;
      setBusy(false);
    }
  };

  // 2026-06-20 (user request): input koordinat diganti Google Plus Code —
  // Google Maps me-resolve plus code native, jadi preview cukup link eksternal.
  const hasPlusCode = form.plus_code.trim() !== "";
  const plusCodeHref = hasPlusCode
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(form.plus_code.trim())}`
    : null;

  const addRow = (key, row) => set(key, [...form[key], row]);
  const setRow = (key, i, k, v) => set(key, form[key].map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const delRow = (key, i) => set(key, form[key].filter((_, j) => j !== i));

  return (
    <div className="p-6 pb-28 space-y-4 max-w-5xl" data-testid="new-project-page">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1.5">
        <Link to="/projects" className="hover:text-[#0B1D3A]">Proyek</Link>
        <span className="text-slate-400">›</span>
        <span className="text-slate-700">New Project</span>
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0B1D3A] tracking-tight" data-testid="np-title">Buat Project Baru</h1>
        {draftAt && <span className="text-[10px] text-slate-400" data-testid="np-draft-info">Draft dipulihkan · tersimpan {new Date(draftAt).toLocaleString("id-ID")}</span>}
      </div>

      {/* 1. Informasi Project */}
      <Section icon={Building2} title="Informasi Project" testid="np-section-info">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 2026-06-20 (user request): kode proyek bisa diedit — kosongkan untuk kode otomatis PRJ-XXXXXX. */}
          <div>
            <label className="overline text-[9px] text-slate-600">Kode Proyek</label>
            <input data-testid="np-code" value={form.project_code}
              onChange={(e) => set("project_code", e.target.value.toUpperCase())}
              placeholder="PRJ-XXXXXX (otomatis bila dikosongkan)"
              className="mt-1 w-full border border-slate-300 px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:border-[#0B1D3A]" />
            <div className="text-[10px] text-slate-400 mt-1">Kosongkan untuk memakai kode otomatis. Kode kustom harus unik.</div>
          </div>
          <Inp testid="np-name" label="Nama Proyek" required error={errors.project_name}
            value={form.project_name} onChange={(e) => set("project_name", e.target.value)} placeholder="cth: RKI - RF Adam Malik" />
          <Inp testid="np-client" label="Client" required error={errors.client}
            value={form.client} onChange={(e) => set("client", e.target.value)} placeholder="cth: PT. Richeese Kuliner Indonesia" />
          <Inp testid="np-contract-number" label="Nomor Kontrak / PO"
            value={form.contract_number} onChange={(e) => set("contract_number", e.target.value)} placeholder="cth: PO-60046634" />
          <Inp testid="np-contract-value" label="Nilai Kontrak (Rp)" error={errors.contract_value}
            value={form.contract_value} onChange={(e) => set("contract_value", e.target.value.replace(/[^0-9.]/g, ""))} inputMode="numeric" placeholder="cth: 100000000" />
          <div>
            <label className="overline text-[9px] text-slate-600">PIC Project (divisi Project)</label>
            <select data-testid="np-pic" value={form.pic_name} onChange={(e) => set("pic_name", e.target.value)}
              className="mt-1 w-full border border-slate-300 px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:border-[#0B1D3A]">
              <option value="">— Belum ditentukan —</option>
              {form.pic_name && !picProjectOptions.includes(form.pic_name) && <option value={form.pic_name}>{form.pic_name} (saat ini)</option>}
              {picProjectOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="overline text-[9px] text-slate-600">PIC Keuangan (bebas — siapa saja yang ditunjuk)</label>
            <select data-testid="np-pic-finance" value={form.pic_finance_name} onChange={(e) => set("pic_finance_name", e.target.value)}
              className="mt-1 w-full border border-slate-300 px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:border-[#0B1D3A]">
              <option value="">— Belum ditentukan —</option>
              {picFinanceOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <Inp testid="np-start" label="Tanggal Mulai" required error={errors.start_date} type="date"
            value={form.start_date} onChange={(e) => set("start_date", e.target.value)} />
          <Inp testid="np-end" label="Tanggal Selesai" error={errors.end_date} type="date"
            value={form.end_date} onChange={(e) => set("end_date", e.target.value)} />
        </div>
      </Section>

      {/* 2. Lokasi Project */}
      <Section icon={MapPin} title="Lokasi Project" testid="np-section-lokasi">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Inp testid="np-address" label="Alamat"
              value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Alamat lengkap lokasi proyek" />
          </div>
          <Inp testid="np-city" label="Kota"
            value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="cth: Medan" />
          <Inp testid="np-province" label="Provinsi"
            value={form.province} onChange={(e) => set("province", e.target.value)} placeholder="cth: Sumatera Utara" />
          <div className="md:col-span-2">
            <Inp testid="np-plus-code" label="Google Plus Code"
              value={form.plus_code} onChange={(e) => set("plus_code", e.target.value)}
              placeholder="cth: 6PFG57QM+Q9C  atau  57QM+Q9 Medan" />
            <div className="text-[10px] text-slate-400 mt-1">
              Buka Google Maps → klik lokasi proyek → salin kode (mis. <span className="font-mono">57QM+Q9 Medan</span>) ke kolom ini.
            </div>
          </div>
        </div>
        <div className="mt-3">
          {hasPlusCode ? (
            <a href={plusCodeHref} target="_blank" rel="noreferrer"
              data-testid="np-open-maps" className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold">
              <ExternalLink size={11} /> Cek Lokasi di Google Maps
            </a>
          ) : (
            <div className="h-16 border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-[11px] text-slate-400 text-center px-3" data-testid="np-map-empty">
              Isi Google Plus Code untuk menautkan lokasi proyek ke Google Maps.
            </div>
          )}
        </div>
      </Section>

      {/* 3. Ruang Lingkup Pekerjaan */}
      <Section icon={FileText} title="Ruang Lingkup Pekerjaan" testid="np-section-scope">
        <div className="text-[11px] text-slate-500 mb-2">Deskripsi pekerjaan + jenis/kategori + bobot (%) — opsional, bisa dilengkapi kemudian.</div>
        {form.scope_of_work.map((s, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`np-scope-row-${i}`}>
            <div className="col-span-6"><Inp testid={`np-scope-desc-${i}`} label="Deskripsi Pekerjaan" value={s.description} onChange={(e) => setRow("scope_of_work", i, "description", e.target.value)} /></div>
            <div className="col-span-3"><Inp testid={`np-scope-cat-${i}`} label="Jenis / Kategori" value={s.category} onChange={(e) => setRow("scope_of_work", i, "category", e.target.value)} /></div>
            <div className="col-span-2"><Inp testid={`np-scope-weight-${i}`} label="Bobot (%)" value={s.weight_pct} onChange={(e) => setRow("scope_of_work", i, "weight_pct", e.target.value.replace(/[^0-9.]/g, ""))} inputMode="numeric" /></div>
            <button type="button" data-testid={`np-scope-del-${i}`} onClick={() => delRow("scope_of_work", i)} className="col-span-1 p-2 text-rose-500 hover:text-rose-700"><Trash2 size={14} /></button>
          </div>
        ))}
        <button type="button" data-testid="np-scope-add" onClick={() => addRow("scope_of_work", { description: "", category: "", weight_pct: "" })}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-dashed border-slate-300 text-[11px] text-slate-600 hover:border-[#0B1D3A] hover:text-[#0B1D3A]">
          <Plus size={12} /> Tambah Pekerjaan
        </button>
      </Section>

      {/* 4. Kontak Project */}
      <Section icon={Users} title="Kontak Project" testid="np-section-kontak">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-2">Client PIC</div>
        {form.client_contacts.map((c, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`np-contact-row-${i}`}>
            <div className="col-span-4"><Inp testid={`np-contact-name-${i}`} label="Nama" value={c.name} onChange={(e) => setRow("client_contacts", i, "name", e.target.value)} /></div>
            <div className="col-span-3"><Inp testid={`np-contact-role-${i}`} label="Jabatan" value={c.role} onChange={(e) => setRow("client_contacts", i, "role", e.target.value)} /></div>
            <div className="col-span-2"><Inp testid={`np-contact-phone-${i}`} label="Telepon" value={c.phone} onChange={(e) => setRow("client_contacts", i, "phone", e.target.value)} /></div>
            <div className="col-span-2"><Inp testid={`np-contact-email-${i}`} label="Email" value={c.email} onChange={(e) => setRow("client_contacts", i, "email", e.target.value)} /></div>
            <button type="button" data-testid={`np-contact-del-${i}`} onClick={() => delRow("client_contacts", i)} className="col-span-1 p-2 text-rose-500 hover:text-rose-700"><Trash2 size={14} /></button>
          </div>
        ))}
        <button type="button" data-testid="np-contact-add" onClick={() => addRow("client_contacts", { name: "", role: "", phone: "", email: "" })}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-dashed border-slate-300 text-[11px] text-slate-600 hover:border-[#0B1D3A] hover:text-[#0B1D3A]">
          <Plus size={12} /> Tambah Kontak Client
        </button>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mt-4 mb-2">Site Contact</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Inp testid="np-site-name" label="Nama" value={form.site_name} onChange={(e) => set("site_name", e.target.value)} />
          <Inp testid="np-site-phone" label="Telepon" value={form.site_phone} onChange={(e) => set("site_phone", e.target.value)} />
          <Inp testid="np-site-email" label="Email" value={form.site_email} onChange={(e) => set("site_email", e.target.value)} />
        </div>
      </Section>

      {/* 5. Dokumen Project */}
      <Section icon={FileText} title="Dokumen Project" testid="np-section-docs">
        <div className="text-[11px] text-slate-500 mb-2">Dokumen bisa ditambahkan setelah data dasar proyek valid. Isi nama, jenis, versi, tanggal, status, dan tautan file.</div>
        {form.project_documents.map((d, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`np-doc-row-${i}`}>
            <div className="col-span-3"><Inp testid={`np-doc-name-${i}`} label="Nama Dokumen" value={d.name} onChange={(e) => setRow("project_documents", i, "name", e.target.value)} /></div>
            <div className="col-span-2"><Inp testid={`np-doc-type-${i}`} label="Jenis" value={d.type} onChange={(e) => setRow("project_documents", i, "type", e.target.value)} placeholder="Kontrak/SPH/PO…" /></div>
            <div className="col-span-1"><Inp testid={`np-doc-version-${i}`} label="Versi" value={d.version} onChange={(e) => setRow("project_documents", i, "version", e.target.value)} /></div>
            <div className="col-span-2"><Inp testid={`np-doc-date-${i}`} label="Tanggal" type="date" value={d.date} onChange={(e) => setRow("project_documents", i, "date", e.target.value)} /></div>
            <div className="col-span-1"><Inp testid={`np-doc-status-${i}`} label="Status" value={d.status} onChange={(e) => setRow("project_documents", i, "status", e.target.value)} placeholder="Final" /></div>
            <div className="col-span-2"><Inp testid={`np-doc-url-${i}`} label="Tautan File" value={d.url} onChange={(e) => setRow("project_documents", i, "url", e.target.value)} placeholder="https://…" /></div>
            <button type="button" data-testid={`np-doc-del-${i}`} onClick={() => delRow("project_documents", i)} className="col-span-1 p-2 text-rose-500 hover:text-rose-700"><Trash2 size={14} /></button>
          </div>
        ))}
        <button type="button" data-testid="np-doc-add" onClick={() => addRow("project_documents", { name: "", type: "", version: "", date: "", status: "", url: "" })}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-dashed border-slate-300 text-[11px] text-slate-600 hover:border-[#0B1D3A] hover:text-[#0B1D3A]">
          <Plus size={12} /> Tambah Dokumen
        </button>
      </Section>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200" data-testid="np-action-bar">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <div className="text-[10px] text-slate-400">{Object.keys(errors).filter((k) => errors[k]).length > 0 && <span className="text-rose-600 font-semibold">Ada field yang perlu diperbaiki.</span>}</div>
          <div className="flex items-center gap-2">
            <button type="button" data-testid="np-cancel" onClick={() => nav("/projects")} disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 border border-slate-300 bg-white hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold text-slate-700 disabled:opacity-50">
              <ArrowLeft size={13} /> Batal
            </button>
            <button type="button" data-testid="np-save-draft" onClick={saveDraft} disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#0B1D3A] text-[#0B1D3A] hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold disabled:opacity-50">
              <Save size={13} /> Simpan Draft
            </button>
            <button type="button" data-testid="np-submit" onClick={submit} disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FF5A1F] hover:bg-[#e04a13] text-white text-xs uppercase tracking-wider font-semibold disabled:opacity-50">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />} {busy ? "Membuat…" : "Buat Project"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
